<template>
  <modal
    name="bank-account-transfer-detail"
    :width="720"
    height="auto"
    :adaptive="true"
    :scrollable="true"
    :click-to-close="!detailLoading"
    @closed="resetDetail"
  >
    <div class="modal-form bank-transfer-modal">
      <div class="modal-form-header">
        <div class="d-flex align-items-center min-width-0">
          <span class="modal-title-icon" aria-hidden="true">
            <i class="fas fa-file-invoice-dollar"></i>
          </span>

          <div class="min-width-0">
            <h4 class="modal-title mb-0">Detalle del traspaso</h4>
            <small class="modal-subtitle text-truncate d-block">
              # {{ detailTransfer ? detailTransfer.id : 'Consulta del movimiento bancario' }}
            </small>
          </div>
        </div>

        <button
          type="button"
          class="close ml-3"
          :disabled="detailLoading"
          title="Cerrar"
          aria-label="Cerrar detalle del traspaso"
          @click="closeDetailModal"
        >
          <span aria-hidden="true">&times;</span>
        </button>
      </div>

      <div class="modal-form-body">
        <div v-if="detailError" class="alert alert-danger mb-3" role="alert">
          <div class="d-flex align-items-start justify-content-between">
            <div class="pr-3">
              <i class="fas fa-exclamation-circle mr-1" aria-hidden="true"></i>
              {{ detailError }}
            </div>

            <button
              v-if="currentTransferId"
              type="button"
              class="btn btn-outline-danger btn-sm flex-shrink-0"
              :disabled="detailLoading"
              title="Volver a consultar"
              aria-label="Volver a consultar el detalle"
              @click="retryDetail"
            >
              <i class="fas fa-redo-alt mr-1" aria-hidden="true"></i>
              Reintentar
            </button>
          </div>
        </div>

        <div v-if="detailLoading" class="modal-state" role="status" aria-live="polite">
          <span class="modal-state-icon" aria-hidden="true">
            <i class="fas fa-spinner fa-spin"></i>
          </span>
          <strong>Cargando detalle</strong>
          <small>Estamos consultando la información del traspaso.</small>
        </div>

        <div v-else-if="detailTransfer">
          <section class="transfer-hero" aria-label="Resumen del traspaso">
            <div>
              <small>Monto transferido</small>
              <strong>{{ formatMoney(detailTransfer.amount, detailTransfer) }}</strong>
            </div>

            <span class="status-pill" :class="statusClass(detailTransfer.status)">
              <i :class="statusIcon(detailTransfer.status)" class="mr-1" aria-hidden="true"></i>
              {{ statusLabel(detailTransfer.status) }}
            </span>
          </section>

          <section class="transfer-route mt-3" aria-label="Ruta del traspaso">
            <div class="account-panel">
              <span class="account-icon account-icon-source" aria-hidden="true">
                <i class="fas fa-university"></i>
              </span>
              <div class="account-content">
                <small>Cuenta origen</small>
                <strong :title="accountName(detailTransfer.source_account)">
                  {{ accountName(detailTransfer.source_account) }}
                </strong>
                <span :title="accountSecondary(detailTransfer.source_account)">
                  {{ accountSecondary(detailTransfer.source_account) || 'Sin datos bancarios adicionales' }}
                </span>
              </div>
            </div>

            <span class="route-arrow" aria-hidden="true">
              <i class="fas fa-arrow-right"></i>
            </span>

            <div class="account-panel">
              <span class="account-icon account-icon-destination" aria-hidden="true">
                <i class="fas fa-university"></i>
              </span>
              <div class="account-content">
                <small>Cuenta destino</small>
                <strong :title="accountName(detailTransfer.destination_account)">
                  {{ accountName(detailTransfer.destination_account) }}
                </strong>
                <span :title="accountSecondary(detailTransfer.destination_account)">
                  {{ accountSecondary(detailTransfer.destination_account) || 'Sin datos bancarios adicionales' }}
                </span>
              </div>
            </div>
          </section>

          <section class="detail-section mt-3" aria-labelledby="transfer-general-data">
            <h5 id="transfer-general-data" class="section-title">Información general</h5>

            <div class="row detail-grid">
              <div class="col-6 col-md-3 mb-3">
                <div class="detail-item">
                  <small>Fecha</small>
                  <strong>{{ formatDate(detailTransfer.transfer_date) }}</strong>
                </div>
              </div>

              <div class="col-6 col-md-3 mb-3">
                <div class="detail-item">
                  <small>Tipo</small>
                  <strong>{{ paymentTypeName(detailTransfer) }}</strong>
                </div>
              </div>

              <div class="col-6 col-md-3 mb-3">
                <div class="detail-item">
                  <small>Empresa</small>
                  <strong :title="companyName(detailTransfer.company)">
                    {{ companyName(detailTransfer.company) }}
                  </strong>
                </div>
              </div>

              <div class="col-6 col-md-3 mb-3">
                <div class="detail-item">
                  <small>Registró</small>
                  <strong :title="userName(detailTransfer.created_by)">
                    {{ userName(detailTransfer.created_by) }}
                  </strong>
                </div>
              </div>
            </div>
          </section>

          <section v-if="detailTransfer.comment" class="detail-comment mt-3">
            <small>Comentario</small>
            <p class="mb-0">{{ detailTransfer.comment }}</p>
          </section>

          <section v-if="isCancelled(detailTransfer.status)" class="cancellation-box mt-3">
            <div class="d-flex align-items-start">
              <span class="cancellation-icon" aria-hidden="true">
                <i class="fas fa-ban"></i>
              </span>

              <div class="min-width-0">
                <strong>Traspaso cancelado mediante movimientos inversos</strong>
                <p class="mb-1 mt-1">
                  {{ detailTransfer.cancellation_comment || 'Sin comentario de cancelación.' }}
                </p>
                <small>
                  {{ formatDateTime(detailTransfer.cancelled_at) }}
                  <template v-if="detailTransfer.cancelled_by">
                    · {{ userName(detailTransfer.cancelled_by) }}
                  </template>
                </small>
              </div>
            </div>
          </section>
        </div>

        <div v-else class="modal-state" role="status">
          <span class="modal-state-icon" aria-hidden="true">
            <i class="fas fa-search"></i>
          </span>
          <strong>Detalle no disponible</strong>
          <small>No fue posible identificar el traspaso solicitado.</small>
        </div>
      </div>

      <div class="modal-form-footer">
        <small v-if="detailTransfer && detailTransfer.created_at" class="footer-meta">
          Registrado el {{ formatDateTime(detailTransfer.created_at) }}
        </small>
        <span v-else></span>

        <button
          type="button"
          class="btn btn-outline-secondary btn-sm"
          :disabled="detailLoading"
          @click="closeDetailModal"
        >
          Cerrar
        </button>
      </div>
    </div>
  </modal>
</template>

<script>
import request from '@/services/request';

export default {
  name: 'ModalBankAccountTransferDetail',

  data() {
    return {
      detailLoading: false,
      detailError: '',
      detailTransfer: null,
      currentTransferId: null,
      detailRequestSequence: 0,
    };
  },

  methods: {
    closeDetailModal() {
      if (!this.detailLoading) {
        this.$modal.hide('bank-account-transfer-detail');
      }
    },

    resetDetail() {
      this.detailRequestSequence += 1;
      this.detailLoading = false;
      this.detailError = '';
      this.detailTransfer = null;
      this.currentTransferId = null;
    },

    openDetailModal(transfer) {
      this.detailError = '';
      this.detailTransfer = transfer && typeof transfer === 'object' ? transfer : null;
      this.currentTransferId = transfer && transfer.id ? transfer.id : null;
      this.$modal.show('bank-account-transfer-detail');

      if (!this.currentTransferId) {
        this.detailError = 'No fue posible identificar el traspaso seleccionado.';
        return;
      }

      this.loadDetail(this.currentTransferId);
    },

    async loadDetail(transferId) {
      const requestSequence = this.detailRequestSequence + 1;
      this.detailRequestSequence = requestSequence;
      this.detailLoading = true;
      this.detailError = '';

      try {
        const response = await request.get(`/bank-account-transfers/${transferId}`);
        const body = this.unwrapHttpResponse(response);
        const transfer = body && (body.data || body.transfer)
          ? (body.data || body.transfer)
          : body;

        if (requestSequence === this.detailRequestSequence) {
          this.detailTransfer = transfer;
        }
      } catch (error) {
        if (requestSequence === this.detailRequestSequence) {
          this.detailError = this.getErrorMessage(error);
        }
      } finally {
        if (requestSequence === this.detailRequestSequence) {
          this.detailLoading = false;
        }
      }
    },

    retryDetail() {
      if (this.currentTransferId) {
        this.loadDetail(this.currentTransferId);
      }
    },

    unwrapHttpResponse(response) {
      if (
        response
        && typeof response.status === 'number'
        && Object.prototype.hasOwnProperty.call(response, 'data')
      ) {
        return response.data;
      }

      return response;
    },

    getErrorMessage(error) {
      const response = error && error.response ? error.response.data : null;
      const body = response || (error && error.data) || null;

      if (body && body.errors && typeof body.errors === 'object') {
        const firstKey = Object.keys(body.errors)[0];
        const firstError = firstKey ? body.errors[firstKey] : null;

        if (Array.isArray(firstError) && firstError.length) {
          return firstError[0];
        }

        if (typeof firstError === 'string') {
          return firstError;
        }
      }

      if (body && body.message) {
        return body.message;
      }

      if (error && error.message) {
        return error.message;
      }

      return 'Ocurrió un error al consultar el traspaso. Intenta nuevamente.';
    },

    isCancelled(status) {
      return status === 'cancelled';
    },

    statusLabel(status) {
      return this.isCancelled(status) ? 'Cancelado' : 'Aplicado';
    },

    statusClass(status) {
      return this.isCancelled(status)
        ? 'status-pill-cancelled'
        : 'status-pill-posted';
    },

    statusIcon(status) {
      return this.isCancelled(status) ? 'fas fa-ban' : 'fas fa-check-circle';
    },

    companyName(company) {
      if (typeof company === 'string') {
        return company;
      }

      return company && company.name ? company.name : 'No disponible';
    },

    userName(user) {
      if (typeof user === 'string') {
        return user;
      }

      if (!user || typeof user !== 'object') {
        return 'No disponible';
      }

      return user.name || user.full_name || user.email || 'No disponible';
    },

    paymentTypeName(transfer) {
      const type = transfer.type_payment
        || transfer.type_payments
        || transfer.typePayment
        || null;

      return type && type.name ? type.name : 'Transferencia';
    },

    accountEntity(account) {
      if (!account || typeof account !== 'object') {
        return null;
      }

      return account.bank_account
        || account.bankAccount
        || account.banks_accounts
        || account;
    },

    accountBank(account) {
      const entity = this.accountEntity(account);

      if (!entity) {
        return null;
      }

      return entity.bank || entity.banks || entity.bank_data || null;
    },

    accountName(account) {
      const entity = this.accountEntity(account);

      if (!entity) {
        return 'Cuenta no disponible';
      }

      const bank = this.accountBank(account);

      const accountId = account.id || entity.id || null;

      return entity.name
        || entity.account_name
        || entity.alias
        || (bank && bank.name)
        || (accountId ? `Cuenta #${accountId}` : 'Cuenta no disponible');
    },

    accountSecondary(account) {
      const entity = this.accountEntity(account);

      if (!entity) {
        return '';
      }

      const bank = this.accountBank(account);
      const bankName = bank && bank.name ? bank.name : '';
      const number = entity.account_number
        || entity.number
        || account.account_number
        || account.number
        || '';

      return [bankName, number]
        .filter((value, index, values) => value && values.indexOf(value) === index)
        .join(' · ');
    },

    currencyCode(transfer) {
      const account = transfer && transfer.source_account
        ? this.accountEntity(transfer.source_account)
        : null;
      const currency = account && account.currency ? account.currency : null;
      const code = (currency && (currency.code || currency.iso_code))
        || (account && account.currency_code)
        || (transfer && transfer.currency_code)
        || '';

      return /^[A-Za-z]{3}$/.test(String(code)) ? String(code).toUpperCase() : '';
    },

    formatMoney(value, transfer) {
      const amount = Number(value || 0);
      const normalizedAmount = Number.isFinite(amount) ? amount : 0;
      const currency = this.currencyCode(transfer);

      if (currency) {
        try {
          return new Intl.NumberFormat('es-MX', {
            style: 'currency',
            currency,
            minimumFractionDigits: 2,
            maximumFractionDigits: 2,
          }).format(normalizedAmount);
        } catch (error) {
          if (error && error.name !== 'RangeError') {
            throw error;
          }

          // Usa el formato general cuando el navegador no reconoce la moneda.
        }
      }

      return `$ ${new Intl.NumberFormat('es-MX', {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      }).format(normalizedAmount)}`;
    },

    formatDate(value) {
      if (!value) {
        return 'No disponible';
      }

      const date = new Date(`${String(value).slice(0, 10)}T00:00:00`);

      return Number.isNaN(date.getTime())
        ? 'No disponible'
        : date.toLocaleDateString('es-MX', {
          day: '2-digit',
          month: 'short',
          year: 'numeric',
        });
    },

    formatDateTime(value) {
      if (!value) {
        return 'Fecha no disponible';
      }

      const date = new Date(value);

      return Number.isNaN(date.getTime())
        ? 'Fecha no disponible'
        : date.toLocaleString('es-MX', {
          day: '2-digit',
          month: 'short',
          year: 'numeric',
          hour: '2-digit',
          minute: '2-digit',
        });
    },
  },

  created() {
    Event.$on('event-show-showBankAccountTransferDetail', this.openDetailModal);
  },

  beforeDestroy() {
    Event.$off('event-show-showBankAccountTransferDetail', this.openDetailModal);
  },
};
</script>

<style src="@/assets/css/bank-account-transfer-modals.css"></style>
